import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { emergencyApi } from '../services/api';

export default function EmergencyRequest() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: async () => {
      if (!contactId) throw new Error('No contact ID provided');
      const response = await emergencyApi.requestAccess(contactId, reason || undefined);
      return response.data;
    },
    onSuccess: (data) => {
      // Navigate to the status page if we have request info, otherwise show success
      if (data?.id && data?.token) {
        navigate(`/emergency/access/${data.id}?token=${data.token}`);
      } else {
        // Fallback: show success message
        setError(null);
        alert('Emergency access request submitted successfully. The primary caregiver has been notified.');
        navigate('/');
      }
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to submit emergency access request');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    requestMutation.mutate();
  };

  if (!contactId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invalid Request</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">No contact ID provided.</p>
          <Link to="/emergency" className="mt-4 inline-block btn-primary">
            Start Over
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="max-w-xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <span className="text-3xl">🚨</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Request Emergency Access
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The primary caregiver will be notified immediately
          </p>
        </div>

        {/* Form */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="reason" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for Request (optional)
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Briefly describe the emergency situation..."
                className="input min-h-[120px] text-base"
                maxLength={1000}
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                This information will be shared with the caregiver.
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Auto-approval:</strong> If the caregiver doesn't respond within 24 hours, access will be automatically granted for 7 days.
              </p>
            </div>

            <button
              type="submit"
              disabled={requestMutation.isPending}
              className="btn-danger w-full text-lg py-3"
            >
              {requestMutation.isPending ? (
                <>
                  <span className="spinner mr-2" />
                  Submitting Request...
                </>
              ) : (
                'Submit Emergency Access Request'
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link
              to="/emergency"
              className="block text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Cancel and go back
            </Link>
          </div>
        </div>

        {/* Warning */}
        <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
            Emergency access requests are logged and audited. Misuse may result in access being permanently revoked.
          </p>
        </div>
      </div>
    </div>
  );
}
